import React, { useEffect, useState } from 'react';
import { Download, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LIST_FIELDS, LIST_KINDS, defaultFields, listTable, readListSelection, readListTemplates, saveListTemplate, templateTestId, writeListSelection } from './listDocument';
import { buildListPdf } from './listPdf';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function printHtml(html) {
  const frame = document.createElement('iframe');
  frame.setAttribute('data-testid', 'listen-print-frame');
  frame.setAttribute('title', 'Liste');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  doc.open();
  doc.write(html);
  doc.close();
  try {
    frame.contentWindow?.focus();
    frame.contentWindow?.print?.();
  } catch {
    /* jsdom kennt window.print nicht */
  }
  window.setTimeout(() => frame.remove(), 1000);
}

export function printList({ title, headers, rows }) {
  const head = headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('');
  const body = rows.map((row) => (
    `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`
  )).join('');
  printHtml(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>
      body { font-family: sans-serif; color: #0f172a; }
      h1 { font-size: 16px; }
      table { border-collapse: collapse; width: 100%; font-size: 11px; }
      th, td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; }
      th { background: #f1f5f9; }
    </style></head><body>
    <h1>${escapeHtml(title)}</h1>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    </body></html>`);
}

export function downloadListPdf({ title, headers, rows }) {
  const bytes = buildListPdf({ title, headers, rows });
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/\s+/g, '_')}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

export default function ListenAusdruckeDialog({ open, onClose, enrollments }) {
  const stored = readListSelection();
  const [kind, setKind] = useState(stored?.kind || 'teilnehmer');
  const [fieldsByKind, setFieldsByKind] = useState(stored?.fieldsByKind || {
    teilnehmer: defaultFields('teilnehmer'),
    anwesenheit: defaultFields('anwesenheit'),
  });
  const [templates, setTemplates] = useState(() => readListTemplates());
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    writeListSelection({ kind, fieldsByKind });
  }, [kind, fieldsByKind]);
  const fields = fieldsByKind[kind] || defaultFields(kind);
  const table = listTable(enrollments, kind, fields);

  const applyTemplate = (template) => {
    setKind(template.kind);
    setFieldsByKind((prev) => ({ ...prev, [template.kind]: [...template.fields] }));
  };

  const storeTemplate = () => {
    const name = templateName.trim();
    if (!name) return;
    setTemplates(saveListTemplate({ name, kind, fields }));
    setTemplateName('');
  };

  const toggleField = (id) => {
    setFieldsByKind((prev) => {
      const current = prev[kind] || defaultFields(kind);
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      if (next.length === 0) return prev;
      return { ...prev, [kind]: next };
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="listen-ausdrucke-dialog">
        <DialogHeader>
          <DialogTitle>Listen &amp; Ausdrucke</DialogTitle>
          <DialogDescription>
            {table.rows.length === 1 ? '1 geladener Eintrag der Übersicht.' : `${table.rows.length} geladene Einträge der Übersicht.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Listenart">
          {LIST_KINDS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`listen-kind-${item.id}`}
              aria-pressed={kind === item.id}
              onClick={() => setKind(item.id)}
              className={`text-xs rounded px-3 py-1.5 border ${
                kind === item.id
                  ? 'bg-brand-red text-white border-brand-red'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="listen-templates">
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              data-testid={`listen-template-${templateTestId(template.id)}`}
              onClick={() => applyTemplate(template)}
              className="text-xs rounded px-2 py-1 border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
            >
              {template.name}
            </button>
          ))}
          <input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Vorlage benennen"
            data-testid="listen-template-name"
            className="text-xs h-7 rounded border border-slate-200 px-2 w-40"
          />
          <button
            type="button"
            data-testid="listen-template-save"
            onClick={storeTemplate}
            disabled={!templateName.trim()}
            className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 disabled:opacity-40"
          >
            Vorlage speichern
          </button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="listen-fields">
          {LIST_FIELDS.map((field) => (
            <label key={field.id} className="flex items-center gap-1.5 text-xs text-slate-700">
              <input
                type="checkbox"
                data-testid={`listen-field-${field.id}`}
                checked={fields.includes(field.id)}
                onChange={() => toggleField(field.id)}
              />
              {field.label}
            </label>
          ))}
        </div>
        <div className="overflow-auto max-h-[50vh] border border-slate-200 rounded" data-testid="listen-preview">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                {table.headers.map((header) => (
                  <th key={header} className="text-left py-1.5 px-2 font-medium">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.length === 0 ? (
                <tr>
                  <td className="py-4 px-2 text-slate-400" colSpan={table.headers.length}>Keine Einträge geladen</td>
                </tr>
              ) : table.rows.map((row, index) => (
                <tr key={index} className="border-t border-slate-100">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="py-1 px-2">{cell || ' '}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" data-testid="listen-close" onClick={onClose}>
            Schließen
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="listen-pdf"
            onClick={() => downloadListPdf(table)}
            disabled={table.rows.length === 0}
          >
            <Download className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button
            type="button"
            data-testid="listen-print"
            onClick={() => printList(table)}
            disabled={table.rows.length === 0}
            className="bg-brand-red hover:bg-brand-red/90"
          >
            <Printer className="w-3.5 h-3.5 mr-1" /> Drucken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
